// Canonical domain types & enums shared by the Worker and the web app.
// 64-bit Telegram ids (peer_id, access_hash) are ALWAYS strings on the wire and in D1.

export type PeerType = "group" | "supergroup" | "channel" | "user" | "bot";
export const PEER_TYPES: PeerType[] = ["group", "supergroup", "channel", "user", "bot"];

export type CacheState = "empty" | "fresh" | "stale" | "refreshing";

export type JobStatus =
  | "draft"
  | "materialized"
  | "running"
  | "paused"
  | "peer_flood_halt"
  | "stopped"
  | "completed";

export type TargetStatus =
  | "pending"
  | "sending"
  | "sent"
  | "skipped"
  | "failed"
  | "deferred";

/** Result reported by the browser for a single target after a send attempt. */
export type SendResult = "sent" | "skipped" | "failed" | "deferred";

/** A cached Telegram dialog. */
export interface GroupRecord {
  peerId: string;
  accessHash: string | null;
  peerType: PeerType;
  title: string;
  username: string | null;
  canSend: boolean;
  canSendReason: string | null;
  isCreator: boolean;
  archived: boolean;
  isActive: boolean;
  refreshedAt: number;
  tags: number[];
}

export interface Tag {
  id: number;
  name: string;
  color: string;
  createdAt: number;
  usageCount?: number;
}

export interface ActiveHours {
  startHour: number; // 0-23 local
  endHour: number; // 0-23 local
  tz: string; // IANA tz, e.g. "Europe/Madrid"
}

/** Selection criteria for a broadcast. Frozen into the job at create time. */
export interface TargetFilter {
  mode: "all" | "name" | "tags";
  q?: string;
  tagIds?: number[];
  tagMode?: "any" | "all";
  includeTypes?: PeerType[];
  excludeTypes?: PeerType[];
  activeOnly?: boolean;
  canSendOnly?: boolean;
}

export interface PaceSettings {
  minDelayS: number;
  maxDelayS: number;
  dailyCap: number;
  pauseEveryN?: number | null;
  pauseSecs?: number | null;
  scheduledStartAt?: number | null;
  activeHours?: ActiveHours | null;
}

export interface VariationFlags {
  varyCaption: boolean;
  varyFilename: boolean;
  varyBytes: boolean;
}

/** One image in an album. Up to 10 per message. Always sent as a PHOTO (not a document). */
export interface MediaItem {
  key: string; // R2 object key
  w: number | null;
  h: number | null;
  mime: string;
  size: number;
}

export const MAX_ALBUM = 10;

export type BroadcastMode = "send" | "edit";

export interface Draft extends VariationFlags {
  id: number;
  captionTemplate: string;
  media: MediaItem[];
  baseFileName: string;
  updatedAt: number;
}

export interface Broadcast extends VariationFlags, PaceSettings {
  id: number;
  name: string;
  draftId: number;
  media: MediaItem[];
  captionTemplate: string;
  baseFileName: string;
  targetFilter: TargetFilter;
  status: JobStatus;
  mode: BroadcastMode;
  editOf: number | null;
  riskAckAt: number | null;
  totalTargets: number;
  cooldownUntil: number | null;
  createdAt: number;
  updatedAt: number;
}

/** A row handed to the browser send loop by next-batch (joined with groups). */
export interface NextBatchItem {
  targetId: number;
  peerId: string;
  accessHash: string | null;
  peerType: PeerType;
  username: string | null;
  title: string;
  /** for edit jobs: the message id(s) previously sent to this peer (in album order) */
  messageIds: string[] | null;
}

/** A single result the browser reports back after attempting a target. */
export interface ReportItem {
  targetId: number;
  result: SendResult;
  variant?: string;
  errorCode?: string;
  deferSeconds?: number;
  /** on a successful send: the message id(s) created (album order), stored for later edits */
  messageIds?: string[];
}

export interface ProgressStats {
  status: JobStatus;
  total: number;
  sent: number;
  skipped: number;
  failed: number;
  pending: number;
  deferred: number;
  todaySent: number;
  dailyCap: number;
  cooldownUntil: number | null;
}

/** Error codes that mean a peer is dead → mark group is_active = 0. */
export const DEAD_ERROR_CODES = [
  "CHAT_WRITE_FORBIDDEN",
  "USER_IS_BLOCKED",
  "CHANNEL_PRIVATE",
  "CHAT_ADMIN_REQUIRED",
  "USER_BANNED_IN_CHANNEL",
  "USER_DEACTIVATED",
  "PEER_ID_INVALID",
  "INPUT_USER_DEACTIVATED",
  "ENTITY_UNRESOLVABLE",
] as const;
export type DeadErrorCode = (typeof DEAD_ERROR_CODES)[number];

export function isDeadErrorCode(code: string | undefined): boolean {
  return !!code && (DEAD_ERROR_CODES as readonly string[]).includes(code);
}
